import { useParams, Link } from "wouter";
import { useSEO } from "@/hooks/use-seo";
import { PublicHeader } from "@/components/public-header";
import { PublicFooter } from "@/components/public-footer";
import { Button } from "@/components/ui/button";
import { getSubFeatureBySlug, buildSubFeatureJsonLd, SubFeature, FeatureGroup } from "@/data/feature-catalog";
import { marketingFeatureCatalog as featureCatalog, visibleSubFeatures } from "@/data/marketing-catalog";
import { StartFreeButton, TourButton, START_FREE_LABEL } from "@/components/marketing/cta";
import { signupHref } from "@/lib/marketing/links";
import { subFeatureSEO } from "@/data/feature-seo";
import { useState, useEffect, useRef } from "react";
import {
  CheckCircle,
  ChevronRight,
  MessageSquare,
  Building2,
  Rocket,
  Users,
  Wrench,
  Briefcase,
  DollarSign,
  Plug,
  Inbox,
  Phone,
  MessageCircle,
  Bell,
  Home,
  Map,
  Download,
  Building,
  Calculator,
  TrendingUp,
  Search,
  ClipboardList,
  KeyRound,
  UserCheck,
  Landmark,
  ShoppingBag,
  Hammer,
  Sofa,
  Package,
  FileText,
  Lock,
  Thermometer,
  Camera,
  Store,
  CreditCard,
  Settings,
  BarChart3,
  Receipt,
  RefreshCw,
  AlertCircle,
  Mail,
  Globe,
  ExternalLink,
  Tag,
  Wifi,
  Star,
  Car,
  ClipboardCheck,
  Trash2,
  User,
  Zap,
  Sparkles,
  type LucideIcon,
} from "lucide-react";

const ICON_MAP: Record<string, LucideIcon> = {
  MessageSquare,
  Building2,
  Rocket,
  Users,
  Wrench,
  Briefcase,
  DollarSign,
  Plug,
  Inbox,
  Phone,
  MessageCircle,
  Bell,
  Home,
  Map,
  Download,
  Building,
  Calculator,
  TrendingUp,
  Search,
  ClipboardList,
  KeyRound,
  UserCheck,
  Landmark,
  ShoppingBag,
  Hammer,
  Sofa,
  Package,
  FileText,
  Lock,
  Thermometer,
  Camera,
  Store,
  CreditCard,
  Settings,
  BarChart3,
  Receipt,
  RefreshCw,
  AlertCircle,
  Mail,
  Globe,
  ExternalLink,
  Tag,
  Wifi,
  Star,
  Car,
  ClipboardCheck,
  Trash2,
  User,
  Zap,
  Sparkles,
};

function useScrollAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

function AnimatedSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function MockPreviewPanel({ group, sf }: { group: FeatureGroup; sf: SubFeature }) {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = sf.benefits.slice(0, 3);

  return (
    <div
      className="bg-[#0d1f3c] border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
      data-testid="mock-preview-panel"
      aria-label={`Interactive preview for ${sf.name}`}
    >
      {/* Terminal bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-black/20">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
        <span className="ml-3 text-xs text-white/30 font-mono">padpulse.io · {sf.slug}</span>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-white/10">
        {tabs.map((tab, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors truncate ${
              activeTab === i
                ? `${group.textColor} border-b-2 border-current bg-white/5`
                : "text-white/40 hover:text-white/70 hover:bg-white/5"
            }`}
            data-testid={`mock-tab-${i}`}
          >
            Feature {i + 1}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div className="p-5 space-y-4">
        <div className={`inline-flex items-center gap-2 ${group.bgColor} border ${group.borderColor} rounded-lg px-3 py-1.5`}>
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          <span className={`text-xs font-mono ${group.textColor}`}>
            {sf.slug}.active
          </span>
        </div>

        <p className="text-white/80 text-sm leading-relaxed">{tabs[activeTab]}</p>

        {/* Stats if available */}
        {sf.stats && (
          <div className="grid grid-cols-3 gap-3 mt-2">
            {sf.stats.map((stat, i) => (
              <div
                key={i}
                className={`${group.bgColor} border ${group.borderColor} rounded-xl p-3 text-center`}
              >
                <div className={`text-lg font-bold ${group.textColor}`}>{stat.value}</div>
                <div className="text-xs text-white/40 mt-0.5 leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Progress bar as interactive element */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-white/30">
            <span>Feature utilization</span>
            <span className={group.textColor}>Active</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000`}
              style={{
                width: `${75 + activeTab * 8}%`,
                background: `linear-gradient(90deg, ${group.gradientFrom}, ${group.gradientTo})`,
              }}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 pt-1">
          <button
            onClick={() => setActiveTab((t) => (t + 1) % tabs.length)}
            className={`flex-1 py-2 text-xs font-semibold rounded-lg ${group.bgColor} border ${group.borderColor} ${group.textColor} hover:opacity-80 transition-opacity`}
            data-testid="mock-next-btn"
          >
            Explore Next →
          </button>
          <Link
            href={signupHref()}
            className="flex-1 w-full py-2 text-center text-xs font-semibold rounded-lg bg-brand-accent/90 hover:bg-brand-accent text-white transition-colors"
            data-testid="mock-start-free"
          >
            {START_FREE_LABEL}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function FeaturesSubfeaturePage() {
  const params = useParams<{ groupSlug: string; featureSlug: string }>();
  const result = getSubFeatureBySlug(params.groupSlug, params.featureSlug);

  const seo = result ? subFeatureSEO(result.group, result.subFeature) : null;
  useSEO({
    tabTitle: seo ? seo.tabTitle : "Feature",
    title: seo ? seo.title : "Feature — PadPulse",
    description: seo
      ? seo.description
      : "Explore PadPulse co-living management platform features.",
    path: result
      ? `/features/${result.group.slug}/${result.subFeature.slug}`
      : "/features",
    jsonLd: result
      ? buildSubFeatureJsonLd(result.group, result.subFeature)
      : undefined,
  });

  if (!result) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <PublicHeader />
        <main className="flex-grow flex items-center justify-center pt-20">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Feature not found</h1>
            <p className="text-gray-500 mb-6">The feature you're looking for doesn't exist.</p>
            <Link href="/features">
              <Button>Browse all features</Button>
            </Link>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  // Sibling navigation only walks visible features (F8 hides cut ones).
  const group = { ...result.group, subFeatures: visibleSubFeatures(result.group) };
  const sf = result.subFeature;
  const SfIcon = ICON_MAP[sf.icon] ?? Zap;
  const GroupIcon = ICON_MAP[group.icon] ?? Building2;

  const groupSiblings = group.subFeatures.filter((s) => s.slug !== sf.slug);
  const currentIdx = group.subFeatures.findIndex((s) => s.slug === sf.slug);
  const prevSf = currentIdx > 0 ? group.subFeatures[currentIdx - 1] : null;
  const nextSf = currentIdx < group.subFeatures.length - 1 ? group.subFeatures[currentIdx + 1] : null;
  // When this is the last sub-feature in its group, push the visitor on to the
  // next group so the bottom-of-page flow walks through every section in order.
  const groupIdx = featureCatalog.findIndex((g) => g.slug === group.slug);
  const nextGroup =
    !nextSf && groupIdx >= 0 && groupIdx < featureCatalog.length - 1
      ? featureCatalog[groupIdx + 1]
      : null;

  return (
    <div className="min-h-screen flex flex-col bg-gray-950">
      <PublicHeader />

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#0a1628] via-[#0d2447] to-brand pt-28 md:pt-36 pb-16 md:pb-24">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-20 left-10 w-96 h-96 rounded-full blur-3xl opacity-15"
            style={{ background: group.gradientFrom }}
          />
          <div
            className="absolute bottom-0 right-0 w-80 h-80 rounded-full blur-3xl opacity-10"
            style={{ background: group.gradientTo }}
          />
        </div>

        <div className="relative container mx-auto px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-white/40 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-white/70 transition-colors">Home</Link>
            <ChevronRight size={12} />
            <Link href="/features" className="hover:text-white/70 transition-colors">Features</Link>
            <ChevronRight size={12} />
            <Link href={`/features/${group.slug}`} className={`hover:opacity-80 transition-opacity ${group.textColor}`}>
              {group.name}
            </Link>
            <ChevronRight size={12} />
            <span className="text-white/70">{sf.name}</span>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: copy */}
            <div>
              <div className={`inline-flex items-center gap-2 ${group.bgColor} border ${group.borderColor} rounded-full px-4 py-1.5 text-sm font-medium ${group.textColor} mb-6`}>
                <SfIcon size={14} />
                {sf.name}
              </div>

              <h1
                className="text-4xl md:text-5xl font-bold text-white mb-6 leading-[1.1] tracking-tight"
                data-testid="text-sf-hero-heading"
              >
                {sf.headline}
              </h1>

              <p className="text-lg text-blue-100 mb-8 leading-relaxed">
                {sf.description}
              </p>

              <div className="flex flex-wrap gap-3">
                <StartFreeButton ctaId="sf-hero-start-free" location="features_subfeature_hero" className="px-8" />
                <TourButton ctaId="sf-hero-tour" location="features_subfeature_hero" className="px-8" />
              </div>
            </div>

            {/* Right: interactive mock */}
            <div className="hidden lg:block">
              <MockPreviewPanel group={group} sf={sf} />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile mock preview */}
      <div className="lg:hidden bg-[#0a1628] px-6 pb-8">
        <MockPreviewPanel group={group} sf={sf} />
      </div>

      {/* Benefits section */}
      <section className="bg-gray-950 py-16 md:py-24">
        <div className="container mx-auto px-6">
          <AnimatedSection>
            <div className="text-center mb-12">
              <div className={`inline-flex items-center gap-2 ${group.bgColor} border ${group.borderColor} rounded-full px-4 py-1.5 text-sm font-medium ${group.textColor} mb-4`}>
                <Zap size={14} />
                Key Benefits
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                What {sf.name} does for you
              </h2>
            </div>
          </AnimatedSection>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-4">
            {sf.benefits.map((benefit, i) => (
              <AnimatedSection key={i} delay={i * 70}>
                <div className={`flex items-start gap-3 bg-gray-900 border ${group.borderColor} rounded-xl p-5 hover:border-white/20 transition-colors`}>
                  <CheckCircle size={18} className={`${group.textColor} mt-0.5 flex-shrink-0`} />
                  <p className="text-gray-300 text-sm leading-relaxed">{benefit}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Stats grid */}
          {sf.stats && (
            <AnimatedSection delay={300}>
              <div className="max-w-2xl mx-auto mt-12 grid grid-cols-3 gap-4">
                {sf.stats.map((stat, i) => (
                  <div
                    key={i}
                    className={`${group.bgColor} border ${group.borderColor} rounded-2xl p-6 text-center`}
                  >
                    <div className={`text-2xl font-bold ${group.textColor} mb-1`}>{stat.value}</div>
                    <div className="text-xs text-white/50 leading-tight">{stat.label}</div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          )}
        </div>
      </section>

      {/* Part of group section */}
      <section className={`py-12 md:py-16 ${group.bgColor} border-y ${group.borderColor}`}>
        <div className="container mx-auto px-6">
          <AnimatedSection>
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-8 h-8 rounded-lg ${group.bgColor} border ${group.borderColor} flex items-center justify-center`}>
                <GroupIcon size={16} className={group.textColor} />
              </div>
              <div>
                <p className="text-white/40 text-xs">Part of</p>
                <Link
                  href={`/features/${group.slug}`}
                  className={`text-sm font-semibold ${group.textColor} hover:opacity-80`}
                >
                  {group.name} →
                </Link>
              </div>
            </div>
            <p className="text-white/60 text-sm max-w-2xl leading-relaxed">{group.description}</p>
          </AnimatedSection>

          {groupSiblings.length > 0 && (
            <AnimatedSection delay={100}>
              <div className="mt-8">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Other {group.name} features</p>
                <div className="flex flex-wrap gap-2">
                  {groupSiblings.map((sib) => {
                    const SibIcon = ICON_MAP[sib.icon] ?? Zap;
                    return (
                      <Link
                        key={sib.slug}
                        href={`/features/${group.slug}/${sib.slug}`}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/20 border ${group.borderColor} ${group.textColor} text-xs font-medium hover:opacity-80 transition-opacity`}
                        data-testid={`link-sibling-${sib.slug}`}
                      >
                        <SibIcon size={11} />
                        {sib.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </AnimatedSection>
          )}
        </div>
      </section>

      {/* Prev / Next navigation */}
      {(prevSf || nextSf || nextGroup) && (
        <section className="bg-gray-900 py-10">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto flex gap-4">
              {prevSf ? (
                <Link
                  href={`/features/${group.slug}/${prevSf.slug}`}
                  className={`flex-1 flex flex-col gap-1 p-4 bg-gray-950 border ${group.borderColor} rounded-xl hover:border-white/20 transition-colors group`}
                  data-testid="link-prev-feature"
                >
                  <span className="text-xs text-white/30">← Previous</span>
                  <span className={`text-sm font-semibold ${group.textColor} group-hover:opacity-80`}>{prevSf.name}</span>
                  <span className="text-xs text-white/40 truncate">{prevSf.tagline}</span>
                </Link>
              ) : <div className="flex-1" />}

              {nextSf ? (
                <Link
                  href={`/features/${group.slug}/${nextSf.slug}`}
                  className={`flex-1 flex flex-col gap-1 p-4 bg-gray-950 border ${group.borderColor} rounded-xl hover:border-white/20 transition-colors group text-right`}
                  data-testid="link-next-feature"
                >
                  <span className="text-xs text-white/30">Next →</span>
                  <span className={`text-sm font-semibold ${group.textColor} group-hover:opacity-80`}>{nextSf.name}</span>
                  <span className="text-xs text-white/40 truncate">{nextSf.tagline}</span>
                </Link>
              ) : nextGroup ? (
                <Link
                  href={`/features/${nextGroup.slug}`}
                  className={`flex-1 flex flex-col gap-1 p-4 bg-gray-950 border ${group.borderColor} rounded-xl hover:border-white/20 transition-colors group text-right`}
                  data-testid="link-next-group"
                >
                  <span className="text-xs text-white/30">Next →</span>
                  <span className={`text-sm font-semibold ${group.textColor} group-hover:opacity-80`}>Check out {nextGroup.name}</span>
                  <span className="text-xs text-white/40 truncate">{nextGroup.tagline}</span>
                </Link>
              ) : <div className="flex-1" />}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-[#0a1628] to-[#0d2447] py-16 md:py-20">
        <div className="container mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Start using {sf.name} today
            </h2>
            <p className="text-blue-200 mb-8 max-w-xl mx-auto">
              Sign up for PadPulse to access {sf.name} and every other feature built for PadSplit hosts.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <StartFreeButton ctaId="sf-footer-start-free" location="features_subfeature_footer" className="px-10" />
              <TourButton ctaId="sf-footer-tour" location="features_subfeature_footer" className="px-8" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
